/**
 * usePortalAccess — how the signed-in user may use a partner portal.
 *  admin   -> has role admin (full access)
 *  staff   -> has an internal_profiles row; level from my_module_permissions()
 *  partner -> real partner / master account (unchanged behaviour)
 * Refetches on window focus so permission changes apply without re-login.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type PortalKey = 'agent_portal' | 'vendor_portal' | 'hospital_portal' | 'guest_portal';
export type PortalAccess =
  | { kind: 'admin' } | { kind: 'partner' } | { kind: 'anon' }
  | { kind: 'staff'; level: 'none' | 'view' | 'edit' };

interface Loose {
  rpc: (fn: string, a?: Record<string, unknown>) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  from: (t: string) => { select: (c: string) => { eq: (k: string, v: string) => { maybeSingle: () => PromiseLike<{ data: unknown; error: unknown }> } } };
}
const client = supabase as unknown as Loose;

async function resolve(portal: PortalKey): Promise<PortalAccess> {
  const { data: u } = await supabase.auth.getUser();
  const uid = u.user?.id;
  if (!uid) return { kind: 'anon' };
  const { data: isAdmin } = await client.rpc('has_role', { _user_id: uid, _role: 'admin' });
  if (isAdmin === true) return { kind: 'admin' };
  const { data: prof } = await client.from('internal_profiles').select('user_id').eq('user_id', uid).maybeSingle();
  if (!prof) return { kind: 'partner' };
  const { data, error } = await client.rpc('my_module_permissions');
  if (error) throw new Error(error.message);
  const row = ((data as { module_key: string; level: string }[]) ?? []).find(r => r.module_key === portal);
  const level = (row?.level as 'none' | 'view' | 'edit') ?? 'none';
  return { kind: 'staff', level };
}

export function usePortalAccess(portal: PortalKey) {
  return useQuery({
    queryKey: ['portal-access', portal],
    queryFn: () => resolve(portal),
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
}
